import type {
  ReflectoStorage,
  ReflectoStorageKey,
  StorageAdapter,
  StorageChange,
  StorageChangeListener,
  StorageChanges,
} from "@reflecto/client-core";
import {
  createStore,
  delMany,
  getMany,
  clear as idbClear,
  keys as idbKeys,
  setMany,
} from "idb-keyval";
// Names imported from `storage.constants.ts` so the service worker
// (`apps/web/src/sw/index.ts`) can open the SAME idb-keyval store without
// redeclaring the literals AND without pulling in this module's side
// effects (BroadcastChannel, listener wiring). A rename of either constant
// is a compile error in both consumers.
import { REFLECTO_IDB_DB_NAME, REFLECTO_IDB_STORE_NAME } from "./storage.constants";

// One IndexedDB database, one object store. The shape mirrors what
// browser.storage.local provides for the extension: a flat keyspace where
// each entry is a single key from ReflectoStorage.
const store = createStore(REFLECTO_IDB_DB_NAME, REFLECTO_IDB_STORE_NAME);

// Cross-tab broadcast — IndexedDB has no native onChanged event, so writes
// publish to a BroadcastChannel that other tabs (and the SW) listen on.
// The SW will subscribe to the same channel name when push delivery lands
// in PR 8/10.
const CHANNEL_NAME = "reflecto-storage";
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;

if (!channel) {
  // Surface the degradation rather than silently dropping cross-tab events.
  // Hits in environments without BroadcastChannel (very old browsers,
  // certain test runners). Multiple tabs in such an environment will not
  // see each other's writes — they will diverge until a reload.
  console.warn("[webStorage] BroadcastChannel unavailable — cross-tab change events disabled");
}

const listeners = new Set<StorageChangeListener>();

function fanOut(changes: StorageChanges) {
  for (const listener of listeners) listener(changes);
}

if (channel) {
  channel.onmessage = (e: MessageEvent<StorageChanges>) => fanOut(e.data);
}

function publish(changes: StorageChanges) {
  fanOut(changes);
  channel?.postMessage(changes);
}

export const webStorage: StorageAdapter = {
  async get<K extends ReflectoStorageKey>(...keys: K[]) {
    if (keys.length === 0) return {} as Pick<Partial<ReflectoStorage>, K>;
    const values = await getMany(keys, store);
    const out: Partial<ReflectoStorage> = {};
    keys.forEach((key, idx) => {
      const value = values[idx];
      if (value !== undefined) {
        (out as Record<string, unknown>)[key] = value;
      }
    });
    return out as Pick<Partial<ReflectoStorage>, K>;
  },

  async set(partial) {
    const entries = Object.entries(partial) as [ReflectoStorageKey, unknown][];
    if (entries.length === 0) return;

    // Read-then-write is not transactional. Under truly concurrent writes
    // (multi-tab + SW), oldValue in the change event may reflect a value
    // that another writer has already superseded. Fine for the current
    // single-writer use case; revisit if a consumer ever depends on
    // oldValue being precise.
    const keys = entries.map(([k]) => k);
    const oldValues = await getMany(keys, store);
    await setMany(entries, store);

    // writeChanges() below treats changes as a mutable string-keyed record.
    // At the public boundary it's read back as the per-key-typed
    // StorageChanges; the write-side unknowns are safe because every value
    // originated from `webStorage.set(partial: Partial<ReflectoStorage>)`.
    const changes: Record<string, StorageChange> = {};
    entries.forEach(([key, newValue], idx) => {
      changes[key] = { oldValue: oldValues[idx], newValue };
    });
    publish(changes as StorageChanges);
  },

  async remove(...keys) {
    if (keys.length === 0) return;
    const oldValues = await getMany(keys, store);
    await delMany(keys, store);
    const changes: Record<string, StorageChange> = {};
    keys.forEach((key, idx) => {
      changes[key] = { oldValue: oldValues[idx], newValue: undefined };
    });
    publish(changes as StorageChanges);
  },

  async clear() {
    const allKeys = (await idbKeys(store)) as ReflectoStorageKey[];
    if (allKeys.length === 0) return;
    // Snapshot before clearing so we can emit per-key change events.
    const oldValues = await getMany(allKeys, store);
    await idbClear(store);
    const changes: Record<string, StorageChange> = {};
    allKeys.forEach((key, idx) => {
      changes[key] = { oldValue: oldValues[idx], newValue: undefined };
    });
    publish(changes as StorageChanges);
  },

  onChanged(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

/**
 * Wipe every key EXCEPT the ones in `preserve`. Preserved keys are
 * excluded from the delete set entirely; the rest are removed in one
 * `delMany` transaction, so a concurrent reader never sees a partial
 * delete.
 *
 * Authoritative rationale (callers should point here, not restate it):
 * `clearLocalState` uses this to keep the X25519 device keypair
 * (`publicKey`/`secretKey`) alive across the disconnect/pairing-lost
 * wipe. The keypair is device identity — useless without the partner
 * pubkey (which IS wiped) and regenerated by `ensureKeypair` only when
 * absent. Wiping it is what strands a device "connected but
 * undecryptable" when a stray/racing wipe (e.g. a stale tab's 401
 * `pairing_lost` on the shared IndexedDB) lands mid-re-pair, clobbering
 * the keypair while `completePairing`'s later token/phonePubKey writes
 * survive; preserving it bounds the worst case to a clean re-pair
 * prompt. Full `webStorage.clear()` stays intact for genuine resets.
 */
export async function clearExcept(preserve: ReflectoStorageKey[]): Promise<void> {
  const allKeys = (await idbKeys(store)) as ReflectoStorageKey[];
  const toDelete = allKeys.filter((key) => !preserve.includes(key));
  if (toDelete.length === 0) return;
  // Snapshot before deleting so we can emit per-key change events, matching clear().
  const oldValues = await getMany(toDelete, store);
  await delMany(toDelete, store);
  const changes: Record<string, StorageChange> = {};
  toDelete.forEach((key, idx) => {
    changes[key] = { oldValue: oldValues[idx], newValue: undefined };
  });
  publish(changes as StorageChanges);
}

/**
 * Test-only: drains the module-level listener Set so a failed test that
 * forgets to unsubscribe doesn't leak listeners into subsequent tests.
 * Guarded by `import.meta.env.TEST` so production callers cannot drain
 * the production listener registry even if they imported this symbol —
 * the function is a no-op outside the Vitest environment.
 */
export function _resetForTests(): void {
  if (!import.meta.env.TEST) return;
  listeners.clear();
}
